import React, { useEffect, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter, Search, Toolbar, VirtualScroll } from '@syncfusion/ej2-react-grids';

import useFetchDeclarations from '../hooks/useFetchDeclarations';
import useFetchIntervenants from '../hooks/useFetchIntervenants';
import useFetchDommages from '../hooks/useFetchDommages';

import ListDommage from './ListDommage';
import ListIntervenant from './ListIntervenant';

import { formatCurrency, formatDate } from '../utils/Format';
import FormattedInput from "../utils/FormattedInput";
import CustomSelect from '../utils/CustomSelect';

const statusTemplate = (props) => {
	const normalizedStatus = props.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

	const statusColors = {
		'garanti': 'bg-green-500',
		'non garanti': 'bg-red-500',
		'en attente': 'bg-yellow-500',
		'pre-rapport': 'bg-blue-500',
		'devis valide': 'bg-purple-500',
		'ouvert': 'bg-green-500',
		'sans suite': 'bg-red-700',
	};

	let statusColor = 'bg-white';
	for (const [key, value] of Object.entries(statusColors)) {
		if (normalizedStatus.startsWith(key)) {
			statusColor = value;
			break;
		}
	}

	return (
		<span className={`px-2 py-1 rounded text-white ${statusColor}`}>
			{props.Etat}
		</span>
	);
};

const FinanceTab = ({ lstDommages, declaration, refreshDommages, refreshDeclaration }) => {

    const { fetchDeclarationCalculDommage, fetchUpdateDeclaration } = useFetchDeclarations();

    const { intervenants, fetchIntervenants, detailsProvisions, fetchDetailProvisionsByDommageUUID, fetchDeleteDetailProvisionByUUID,
        fetchAddDetailProvision, fetchUpdateDetailProvision, declarationIntervenants, fetchDeclaIntByDeclaUUID } = useFetchIntervenants();

    const { saveDommageUpdate } = useFetchDommages();

    const [financeTab, setFinanceTab] = useState('parDommage');

    const [selectedRow, setSelectedRow] = useState(null);
    const [editedRow, setEditedRow] = useState(null);
    const [isModified, setIsModified] = useState(false);

    const [showPopup, setShowPopup] = useState(false);
    const [showListIntervenantPopup, setShowListIntervenantPopup] = useState(false);

    const [newDetailProvision, setNewDetailProvision] = useState({
        Nom: '',
        _Type: '',
        Provision_Intervenant: 0,
        Recours_Intervenant: 0
    });

    useEffect(() => {
        if (lstDommages.length > 0 && !selectedRow) {
            setSelectedRow(lstDommages[0]);
        }
    }, [lstDommages, selectedRow]);

    useEffect(() => {
        fetchIntervenants();
    }, []);

    useEffect(() => {
        if (financeTab === "total") {
            fetchDeclarationCalculDommage(declaration.UUID_);
            fetchDeclaIntByDeclaUUID(declaration.UUID_);
            refreshDeclaration();
        }
    }, [financeTab]);

    useEffect(() => {
        if (selectedRow) {
            setEditedRow({ ...selectedRow });
            setIsModified(false);
        }
    }, [selectedRow]);

    const handleFinanceChange = async (tab) => {
        if (tab == "financeTab") {
            await fetchDeclarationCalculDommage(declaration.UUID_);
        }
        setFinanceTab(tab);
    };

    const handleValueChange = (field, newValue) => {
        setEditedRow(prevState => {
            const updatedRow = { ...prevState, [field]: newValue };
            const modified = JSON.stringify(updatedRow) !== JSON.stringify(selectedRow);
            setIsModified(modified);

            return updatedRow;
        });
    };

    const handleValueChangeDeclaration = async (field, newValue) => {
        if (declaration[field] === newValue) return;
        try {
            await fetchUpdateDeclaration({ ...declaration, [field]: newValue });

            if (["Franchise_PP"].includes(field)) {
                await fetchDeclarationCalculDommage(declaration.UUID_);
                refreshDeclaration();
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour de la déclaration :", error);
        }
    };

    const [resultatTravaux, setResultatTravaux] = useState(0);
    useEffect(() => {
        if (editedRow) {
            const result = (editedRow.Recette_Trav_Vol ?? 0) - (editedRow.Depenses_Trav_Vol ?? 0);
            setResultatTravaux(result);
        }
    }, [editedRow?.Recette_Trav_Vol, editedRow?.Depenses_Trav_Vol]);

    const handleSave = async () => {
        if (!isModified) return;
        setSelectedRow({ ...editedRow });
        setIsModified(false);

        try {
            await saveDommageUpdate(editedRow);
            console.log("Données mises à jour :", editedRow);

            refreshDommages();
        } catch (error) {
            console.error("Erreur lors de la mise à jour :", error);
        }
    };

    const handleRestore = () => {
        if (!isModified) return;
        setEditedRow({ ...selectedRow });
        setNewDetailProvision({ Nom: '', _Type: '', Provision_Intervenant: 0, Recours_Intervenant: 0 });
        setIsModified(false);
    };

    const handleDeleteDetailProvision = (intervenant) => {
        if (!window.confirm("Voulez-vous vraiment supprimer cette ligne ?")) return;
        fetchDeleteDetailProvisionByUUID(intervenant.UUID_);
        refreshDetailsProvisions();
    };

    const handleRowDoubleClickDetPro = (event) => {
        const clickedRowData = event.rowData;
        setNewDetailProvision({
            UUID_: clickedRowData.UUID_, Nom: clickedRowData.Ste_Intervenant, _Type: clickedRowData.Type_Intervenant,
            Provision_Intervenant: clickedRowData.Provision_Intervenant, Recours_Intervenant: clickedRowData.Recours_Intervenant
        });
        setShowPopup([true, "edit"]);
    };

    const handleRowDoubleClickLstIntervenant = (event) => {
        setShowListIntervenantPopup(false);
    };

    useEffect(() => {
        if (selectedRow && selectedRow.UUID_ !== editedRow?.UUID_) {
            fetchDetailProvisionsByDommageUUID(selectedRow.UUID_);
        }
    }, [selectedRow]);

    useEffect(() => {
        if (!detailsProvisions || !selectedRow) return;
        let sommeRecoursDetailProvisions = 0;
        let sommeProvisionsDetailProvisions = 0;
        detailsProvisions.forEach((intervenant) => {
            sommeRecoursDetailProvisions += intervenant.Recours_DetailProvision;
            sommeProvisionsDetailProvisions += intervenant.Provision_DetailProvision;
        });
        selectedRow.Somme_Recours_DetailProvisions = sommeRecoursDetailProvisions;
        selectedRow.Somme_Provisions_DetailProvisions = sommeProvisionsDetailProvisions;
    }, [detailsProvisions, selectedRow]);

    const handleOpenPopup = () => {
        setShowPopup(true);
    };

    const handleOpenListIntervenantPopup = () => {
        setShowListIntervenantPopup(true);
    };

    const handleCloseListIntervenantPopup = () => {
        setShowListIntervenantPopup(false);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setNewDetailProvision({ Nom: '', _Type: '', Provision_Intervenant: 0, Recours_Intervenant: 0 });
    };

    const handleAddDetailProvision = async () => {
        if (newDetailProvision.Nom === '') return;

        const intervenantData = { ...newDetailProvision, UUID_Dommage: selectedRow.UUID_, UUID_Intervenant: newDetailProvision.UUID_, UUID_Declaration: declaration.UUID_ };

        try {
            await fetchAddDetailProvision(intervenantData);
            refreshDetailsProvisions();
            handleClosePopup();
        } catch (error) {
            console.error("Erreur lors de l'ajout de l'intervenant :", error);
        }
    };

    const handleEditDetailProvision = async () => {
        if (newDetailProvision.Nom === '') return;

        const intervenantData = { ...newDetailProvision };

        console.log("Edit IntervenantData", intervenantData);

        try {
            await fetchUpdateDetailProvision(intervenantData);
            refreshDetailsProvisions();
            handleClosePopup();
        } catch (error) {
            console.error("Erreur lors de l'edition de l'intervenant :", error);
        }
    };

    const refreshDetailsProvisions = () => {
        fetchDetailProvisionsByDommageUUID(selectedRow.UUID_);
    };

    // console.log("SelectedRow", selectedRow);
    // console.log("EditedRow", editedRow);
    // console.log(lstDommages);
    // console.log("newDetailsProvisions", newDetailProvision);
    // console.log(declaration);
    // console.log(declarationIntervenants)

    if (!selectedRow || !editedRow) return <div></div>;

    const formatDeclaIntervenants = declarationIntervenants.map((intervenant) => {
        return {
            ...intervenant,
            Recour: formatCurrency(intervenant.Recour),
            Franchise: formatCurrency(intervenant.Franchise),
            A_Payer: formatCurrency(intervenant.A_Payer),
            Montant_Regle: formatCurrency(intervenant.Montant_Regle),
            Reste_Du: formatCurrency(intervenant.Reste_Du)
        }
    })

    const formatDetailProvisions = detailsProvisions.map((intervenant) => {
        return {
            ...intervenant,
            Recours_Intervenant: formatCurrency(intervenant.Recours_Intervenant),
            Provision_Intervenant: formatCurrency(intervenant.Provision_Intervenant)
        }
    });

    const columnsDeclaFinance = [
        { field: "Etat", headerText: "État", width: "150px", template: statusTemplate },
        { field: "Libelle", headerText: "Libelle", width: "300px" },
    ];

    console.log(declaration);

    return (
        <div id="form-finances" class="mx-auto">
            <div class="bg-white shadow-lg mb-6 p-4 md:p-8 px-4 rounded-lg">
                <div class="items-center gap-2 grid grid-cols-2 bg-gray-200 mx-auto p-2 rounded-xl w-full sm:w-[25rem]">
                    <div>
                        <input type="radio" name="option" id="1" value="Par Dommage" checked={financeTab === "parDommage"} onChange={() => handleFinanceChange("parDommage")} class="peer hidden" />
                        <label for="1" class="block peer-checked:bg-orange-500 p-2 rounded-xl peer-checked:font-bold peer-checked:text-white text-center transition-all duration-300 ease-in-out cursor-pointer select-none">Par Dommage</label>
                    </div>

                    <div>
                        <input type="radio" name="option" id="2" value="Total" checked={financeTab === "total"} onChange={() => handleFinanceChange("total")} class="peer hidden" />
                        <label for="2" class="block peer-checked:bg-orange-500 p-2 rounded-xl peer-checked:font-bold peer-checked:text-white text-center transition-all duration-300 ease-in-out cursor-pointer select-none">TOTAL</label>
                    </div>
                </div>
                <div class="2xl:flex w-full">
                    {selectedRow && financeTab === "parDommage" && (
                        <>
                            <div class="flex-1 2xl:w-1/2">
                                <ListDommage key={lstDommages.length} lstDommages={lstDommages} size={400} showFilter={false} showAddButton={false} columns={columnsDeclaFinance} setSelectedRow={setSelectedRow} showSummary = {false} />
                            </div>
                            <div class="p-4 2xl:w-1/2">
                                <h2 class="p-4 w-full min-h-[6rem] font-normal text-gray-800 text-lg text-center underline">{selectedRow.Libelle_Court}</h2>
                                <div class="flex justify-between items-center pb-2">
                                    <button onClick={handleRestore} class={`${isModified ? "bg-red-500 hover:bg-red-600 hover:outline-2 hover:outline-red-700 cursor-pointer" : "bg-gray-400 cursor-not-allowed"} inline-flex items-center px-8 py-2 border rounded font-medium text-sm text-white text-sm text-white transition duration-300 ease-in-out hover:outline-none`}>
                                        <i class="mr-2 fa-arrow-rotate-left fa-solid"></i>
                                        Restaurer
                                    </button>
                                    <p class="flexbox text-gray-500">{isModified ? "Dommage modifié" : "Dommage non modifié"}</p>
                                    <button onClick={handleSave} class={`${isModified ? "bg-green-500 hover:bg-green-600 hover:outline-2 hover:outline-green-700 cursor-pointer" : "bg-gray-400 cursor-not-allowed"} inline-flex items-center px-8 py-2 border rounded font-medium text-sm text-white text-sm text-white transition duration-300 ease-in-out hover:outline-none`}>
                                        <i class="mr-2 fa-floppy-disk fa-solid"></i>
                                        Sauvegarder
                                    </button></div>
                                <div class="bg-white border border-gray-200 rounded-sm w-full">
                                    <header class="px-5 border-gray-100 border-b">
                                        <h2 class="font-semibold text-gray-800 text-lg">Tableau des Recours et Provisions</h2>
                                    </header>
                                    <div class="p-3">
                                        <div class="overflow-x-auto">
                                            <table class="w-full table-auto">
                                                <thead class="bg-gray-50 font-semibold text-gray-400 text-sm uppercase">
                                                    <tr>
                                                        <th class="p-2 whitespace-nowrap"></th>
                                                        <th class="p-2 text-center whitespace-nowrap">Recours</th>
                                                        <th class="p-2 text-center whitespace-nowrap">Provision</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-100 font-semibold text-base">
                                                    <tr>
                                                        <td class="p-2 font-medium text-gray-800">Total</td>
                                                        <td class="p-2 text-green-500 text-center">{formatCurrency(detailsProvisions.length > 0 ? editedRow.Recours_PP + detailsProvisions.reduce((sum, i) => sum + i.Recours_Intervenant, 0) : editedRow.Recours_PP)}</td>
                                                        <td class="p-2 text-red-500 text-center">{formatCurrency(detailsProvisions.length > 0 ? editedRow.Provision_PP + detailsProvisions.reduce((sum, i) => sum + i.Provision_Intervenant, 0) : editedRow.Provision_PP)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td class="p-2 font-medium text-gray-800">Part Propre</td>
                                                        <td class="p-2 text-green-500 text-center">
                                                            <FormattedInput
                                                                value={editedRow?.Recours_PP ?? 0}
                                                                onChange={(newValue) => handleValueChange("Recours_PP", newValue)}
                                                            />
                                                        </td>
                                                        <td class="p-2 text-red-500 text-center">
                                                            <FormattedInput
                                                                value={editedRow?.Provision_PP ?? 0}
                                                                onChange={(newValue) => handleValueChange("Provision_PP", newValue)}
                                                            />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex mt-4 border rounded-sm">
                                    <div class="flex flex-1 items-center p-4 transform">
                                        <div class="flex flex-col justify-center items-center bg-gray-100 mr-4 rounded-md w-10 h-10"><i class="fa-money-bill-wave fa-solid"></i></div>
                                        <div class="flex-1 pl-1">
                                            <div class="font-medium">Coût Expertise</div>
                                            <div class="text-gray-600 text-sm">{formatCurrency(selectedRow.Cout_expertise)}</div>
                                        </div>
                                    </div>
                                    <div class="flex flex-1 items-center p-4 transform">
                                        <div class="flex flex-col justify-center items-center bg-gray-100 mr-4 rounded-md w-10 h-10"><i class="fa-solid fa-ticket-simple"></i></div>
                                        <div class="flex-1 pl-1">
                                            <div class="font-medium">Ticket Modérateur</div>
                                            <div class="text-gray-600 text-sm">{formatCurrency(selectedRow.Ticket_Moderateur)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 mt-4">
                                    <h2 className="mb-4 font-semibold text-lg">Recours des Intervenants</h2>
                                    <button onClick={handleOpenPopup} className="inline-flex items-center bg-green-500 hover:bg-green-600 px-3 py-2 border rounded hover:outline-2 hover:outline-green-700 hover:outline-none font-medium text-white text-white text-sm text-sm transition duration-300 ease-in-out cursor-pointer">
                                        <i class="fa-plus fa-solid"></i>
                                    </button>
                                </div>


                                <div>
                                    <GridComponent
                                        dataSource={detailsProvisions}
                                        allowSorting={true}
                                        recordDoubleClick={handleRowDoubleClickDetPro}
                                        height={200}
                                        toolbar={['Search']}
                                        searchSettings={{
                                            fields: ['Libelle'],
                                            placeholder: 'Rechercher...',
                                        }}
                                        sortSettings={{
                                            columns: [{ field: '_Date_Crea', direction: 'Descending' }]
                                        }}

                                    >

                                        <ColumnsDirective>
                                            <ColumnDirective field='Ste_Intervenant' headerText='Intervenant' width='180' />
                                            <ColumnDirective field='Type_Intervenant' headerText='Type' width='180' />
                                            <ColumnDirective field='Recours_Intervenant' headerText='Recours' width='180' />
                                            <ColumnDirective field='Provision_Intervenant' headerText='Provision' width='180' />
                                            <ColumnDirective
                                                headerText=""
                                                template={(props) => (
                                                    <button
                                                        onClick={() => handleDeleteDetailProvision(props)}
                                                        className="bg-red-500 px-2 py-1 rounded text-white"
                                                    >
                                                        <i class="fa-solid fa-trash"></i>
                                                    </button>
                                                )}
                                                width="120"
                                            />
                                        </ColumnsDirective>
                                        <Inject services={[Sort, Filter, Search, Toolbar, VirtualScroll]} />
                                    </GridComponent>

                                    {showPopup && (
                                        <div
                                            className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10`}
                                            onClick={(e) => {
                                                const selection = window.getSelection();
                                                if (selection && selection.type === "Range") {
                                                    return;
                                                }
                                                if (e.target === e.currentTarget) {
                                                    handleClosePopup();
                                                }
                                            }}
                                        >

                                            <div className={`z-10 absolute bg-white p-10 rounded-xl w-full sm:max-w-lg transition-all duration-300`}>
                                                <div className="text-center">
                                                    <h2 className="mt-5 font-bold text-gray-900 text-3xl">{showPopup[1] === "edit" ? "Édition d'un Intervenant" : "Ajout d'Intervenant"}</h2>
                                                    <p className="mt-2 text-gray-400 text-sm">
                                                        {showPopup[1] === "edit" ? "Modifiez les caractéristiques d'un Intervenant" : "Sélectionnez un Intervenant afin de lui ajouter un recours"}

                                                    </p>
                                                </div>
                                                <div className="space-y-3 mt-8">
                                                    <div className="space-y-2 grid grid-cols-1">
                                                        <label className="font-semibold text-gray-500 text-sm tracking-wide">Intervenant</label>
                                                        <div className="flex items-center gap-4 w-full">
                                                            <p class={`${newDetailProvision.Nom === '' ? "hidden" : "block"} font-semibold`}>{newDetailProvision.Nom}</p>
                                                            {showPopup[1] !== "edit" && (
                                                                <button onClick={handleOpenListIntervenantPopup} className={`${newDetailProvision.Nom === '' ? "w-full px-8 justify-center" : "w-2 px-4 justify-center"} bg-orange-600 hover:bg-orange-500 flex py-2 border rounded focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 text-sm text-white transition duration-300 ease-in-out focus:outline-none`}>
                                                                    {newDetailProvision.Nom === '' ? "Sélectionnez un Intervenant" : <i class="fa-pen fa-solid"></i>}
                                                                </button>
                                                            )}
                                                        </div>

                                                    </div>
                                                    <div className="space-y-2 grid grid-cols-1">
                                                        <label className="font-semibold text-gray-500 text-sm tracking-wide">
                                                            Details
                                                        </label>
                                                        <div className="flex justify-center items-center w-full">
                                                            <div className="group flex flex-col p-1 border-4 border-dashed rounded-lg w-full h-80 text-center">
                                                                <div className={`flex flex-col justify-center items-center w-full h-full text-center ${showPopup[1] === "edit" ? "gap-6" : ""} 
                                                                ${newDetailProvision.Nom === '' ? "hidden" : "block"}`}>

                                                                    <div class={`flex flex-col min-w-60`}>
                                                                        <div>
                                                                            <span className='font-semibold text-gray-500 text-sm tracking-wide'>Type</span>
                                                                            {/* { options, label, value, onChange } */}
                                                                            <CustomSelect value={newDetailProvision._Type}
                                                                                options={["Entreprise Générale", "Maître d'Oeuvre", "Maître d'Ouvrage", "BET", "Controleur Technique",
                                                                                    "Fabricant", "Co-Traitant", "Sous-Traitant", "Autre"]}
                                                                                onChange={(newValue) => setNewDetailProvision((prev) => ({ ...prev, _Type: newValue }))}
                                                                            />
                                                                        </div>
                                                                        {showPopup[1] !== "edit" && (
                                                                            <>
                                                                                <div>
                                                                                    <span className='font-semibold text-gray-500 text-sm tracking-wide'>Adresse:</span>
                                                                                    <p>{newDetailProvision.Adresse}</p>
                                                                                </div>
                                                                                <div>
                                                                                    <span className='font-semibold text-gray-500 text-sm tracking-wide'>Code Postale / Ville: </span>
                                                                                    <p>{newDetailProvision.CP} / {newDetailProvision.Ville}</p>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>

                                                                    <div class="flex flex-row justify-between items-center">
                                                                        <div>
                                                                            <span className='font-semibold text-gray-500 text-sm tracking-wide'>Recours:</span>
                                                                            <FormattedInput value={newDetailProvision.Recours_Intervenant ?? 0} onChange={(newValue) => setNewDetailProvision((prev) => ({ ...prev, Recours_Intervenant: newValue }))} />
                                                                        </div>
                                                                        <div>
                                                                            <span className='font-semibold text-gray-500 text-sm tracking-wide'>Provision:</span>
                                                                            <FormattedInput value={newDetailProvision.Provision_Intervenant ?? 0} onChange={(newValue) => setNewDetailProvision((prev) => ({ ...prev, Provision_Intervenant: newValue }))} />
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        {showPopup[1] !== "edit" ? (
                                                            <button
                                                                onClick={handleAddDetailProvision}
                                                                className={`flex ${newDetailProvision.Nom !== '' ? "bg-orange-500 hover:bg-orange-600 shadow-lg focus:shadow-outline cursor-pointer" : "bg-gray-400 cursor-not-allowed"} justify-center my-5 p-4 rounded-full w-full font-semibold text-gray-100 tracking-wide ease-in focus:outline-none transition-all duration-300`}
                                                            >
                                                                {newDetailProvision.Nom !== '' ? `Ajouter l'intervenant sur le dommage "${selectedRow.Libelle_Court.substring(0, 50)}..."` : "Pas d'intervenant à ajouter"}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={handleEditDetailProvision}
                                                                className={`flex ${newDetailProvision.Nom !== '' ? "bg-orange-500 hover:bg-orange-600 shadow-lg focus:shadow-outline cursor-pointer" : "bg-gray-400 cursor-not-allowed"} justify-center my-5 p-4 rounded-full w-full font-semibold text-gray-100 tracking-wide ease-in focus:outline-none transition-all duration-300`}
                                                            >
                                                                {newDetailProvision.Nom !== '' ? `Éditer l'intervenant sur le dommage "${selectedRow.Libelle_Court.substring(0, 50)}..."` : "Pas d'intervenant à ajouter"}
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="text-center">
                                                        <button
                                                            type="button"
                                                            onClick={handleClosePopup}
                                                            className="mt-2 text-gray-500 text-sm hover:underline"
                                                        >
                                                            {showPopup[1] === "edit" ? "Annuler les modifications" : "Annuler l'ajout"}

                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`right-80 z-10 bg-white p-10 rounded-xl w-full sm:max-w-4xl ${showListIntervenantPopup ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}`}>
                                                <span
                                                    onClick={handleCloseListIntervenantPopup}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '20px',
                                                        right: '20px',
                                                        fontSize: '30px',
                                                        color: 'red',
                                                        cursor: 'pointer',
                                                    }}
                                                    title="Fermer"
                                                >
                                                    ✖
                                                </span>
                                                <ListIntervenant ExclusivePage={false} lstIntervenants={intervenants} size={400} showAddButton={false} setSelectedRow={setNewDetailProvision} handleRowDoubleClick={handleRowDoubleClickLstIntervenant} />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <h2 className="mb-4 font-semibold text-lg">Travaux Volontaires</h2>
                                <div class="flex mt-4 border rounded-t-sm">
                                    <div class="flex flex-1 items-center p-4 transform">
                                        <div class="flex-1 pl-1">
                                            <div class="font-medium">Recettes:</div>
                                            <FormattedInput
                                                value={editedRow?.Recette_Trav_Vol ?? 0}
                                                onChange={(newValue) => handleValueChange("Recette_Trav_Vol", newValue)}
                                                className='font-semibold text-green-500'
                                            />
                                        </div>
                                    </div>
                                    <div class="flex flex-1 items-center p-4 transform">
                                        <div class="flex-1 pl-1">
                                            <div class="font-medium">Dépenses:</div>
                                            <FormattedInput
                                                value={editedRow?.Depenses_Trav_Vol ?? 0}
                                                onChange={(newValue) => handleValueChange("Depenses_Trav_Vol", newValue)}
                                                className='font-semibold text-red-500'
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-1 items-center p-4 border border-r rounded-b-sm transform">
                                    <div class="flex flex-row flex-1 justify-between px-4">
                                        <div class="font-medium">Résultat:</div>
                                        <div className={`text-gray-600 text-sm font-semibold text-xl ${resultatTravaux < 0 ? 'text-red-500' : 'text-green-500'}`}>
                                            {formatCurrency(resultatTravaux)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {financeTab === "total" && (
                        <>
                            <div className="mt-10 w-full text-sm">
                                {/* === Volet Assurance === */}
                                <h2 className="pb-2 border-b font-semibold text-lg">Volet Assurance</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <tbody>
                                            <tr className="*:p-3 border-b">
                                                <td className="text-gray-700">Recours Propre</td>
                                                <td className="font-semibold">{formatCurrency(declaration.Total_Recour_PP)}</td>

                                                <td className="text-gray-700">Franchises Intervenant</td>
                                                <td className="font-semibold">{formatCurrency(declaration.Total_Recour_Inter)}</td>
                                                <td className="font-semibold text-gray-700">Recours Total</td>
                                                <td className="font-semibold text-base">{formatCurrency(declaration.Montant_Du_Recours)}</td>
                                            </tr>
                                            <tr className="*:p-3 border-b">
                                                <td></td>
                                                <td></td>
                                                <td className="text-gray-700">Franchise Propre</td>
                                                <td>
                                                    <FormattedInput
                                                        value={declaration.Franchise_PP}
                                                        onChange={(val) => handleValueChangeDeclaration("Franchise_PP", val)}
                                                    />
                                                </td>
                                                <td className="font-semibold text-gray-700">Montant Total</td>
                                                <td className="font-semibold text-base">{formatCurrency(declaration.Total_Recours)}</td>
                                            </tr>
                                            <tr className="*:p-3 border-b">
                                                <td className="text-gray-700">Date Recours</td>
                                                <td>
                                                    <FormattedInput
                                                        value={declaration.Date_Recours_PP}
                                                        onChange={(val) => handleValueChangeDeclaration("Date_Recours_PP", val)}
                                                        type="date"
                                                    />
                                                </td>
                                                <td className="text-gray-700">Date Réglement</td>
                                                <td>
                                                    <FormattedInput
                                                        value={declaration.Date_reglement_PP}
                                                        onChange={(val) => handleValueChangeDeclaration("Date_reglement_PP", val)}
                                                        type="date"
                                                    />
                                                </td>
                                                <td className="font-semibold text-gray-700">Remboursées</td>
                                                <td className="font-semibold text-base">{formatCurrency(declaration.Total_Montant_Regle_Inter)}</td>
                                            </tr>
                                            <tr className="bg-gray-100 *:p-3 border-t">
                                                <td></td>
                                                <td></td>
                                                <td></td>
                                                <td></td>
                                                <td className="font-semibold text-gray-700">À Régler</td>
                                                <td className="font-bold text-lg">{formatCurrency(declaration.Total_Reglement)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <h2 className="mt-6 pb-2 border-b font-semibold text-lg">Volet Travaux Volontaires</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <tbody>
                                            <tr className="border-b">
                                                <td className="p-2 text-gray-700">Recettes</td>
                                                <td className="p-2 font-semibold text-green-500">{formatCurrency(declaration.Total_Facture)}</td>
                                                <td className="p-2 text-gray-700">Dépenses</td>
                                                <td className="p-2 font-semibold text-red-500">{formatCurrency(declaration.Total_Depense)}</td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 text-gray-700">Résultat</td>
                                                <td className="p-2 font-semibold">{formatCurrency(declaration.Total_Trav_Vol)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <h2 className="mt-6 pb-2 border-b font-semibold text-lg">GMO (Garantie Maitre d'Ouvrage)</h2>
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <tbody>
                                            <tr>
                                                <td className="p-2 text-gray-700">Montant</td>
                                                <td>
                                                    <FormattedInput
                                                        value={declaration.GMO_Montant}
                                                        onChange={(val) => handleValueChangeDeclaration("GMO_Montant", val)}
                                                    />
                                                </td>
                                                <td className="p-2 text-gray-700">Réglé le</td>
                                                <td>
                                                    <FormattedInput
                                                        value={declaration.GMO_Date_Reglement}
                                                        onChange={(val) => handleValueChangeDeclaration("GMO_Date_Reglement", val)}
                                                        type='date'
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <h2 className="mt-6 pb-2 border-b font-semibold text-lg">Recours des Intervenants</h2>
                                <GridComponent
                                    dataSource={formatDeclaIntervenants}
                                    allowSorting={true}
                                    height={200}
                                    toolbar={['Search']}
                                    searchSettings={{
                                        fields: ['Nom'],
                                        placeholder: 'Rechercher...',
                                    }}
                                    sortSettings={{
                                        columns: [{ field: 'Nom', direction: 'Descending' }]
                                    }}

                                >
                                    <ColumnsDirective>
                                        <ColumnDirective field='Nom' headerText='Société' width='220' />
                                        <ColumnDirective field='Type' headerText='Type' width='180' />
                                        <ColumnDirective field='Recour' headerText='Recour' width='120' />
                                        <ColumnDirective field='Franchise' headerText='Franchise' width='180' />
                                        <ColumnDirective field='A_Payer' headerText='A Payer' width='180' />
                                        <ColumnDirective field='Date_Recour' headerText='Date Recour' width='180' />
                                        <ColumnDirective field='Date_Reglement' headerText='Date Reglement' width='180' />
                                        <ColumnDirective field='Montant_Regle' headerText='Montant 
                                        
                                        Réglé' width='180' />
                                        <ColumnDirective field='Reste_Du' headerText='Reste_Du' width='180' />
                                    </ColumnsDirective>
                                    <Inject services={[Sort, Filter, Search, Toolbar, VirtualScroll]} />
                                </GridComponent>

                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default FinanceTab;